import { Radio, Row, Col } from "antd";
import {
  FormGroup,
  FullWidth,
  Label,
  StyledCheckboxGroup,
  StyledDropdown,
  StyledInput,
  StyledRadioGroup,
  StyledUnsupportedField,
} from "./StyledDynamicForm";
import PropTypes from "prop-types";
import { YES_NO_PAIR } from "../../utils/formGrouping";

const FieldHint = ({ children }) => (
  <p style={{ margin: "6px 0 0", color: "#6b7280", fontSize: 12, lineHeight: 1.4 }}>{children}</p>
);

FieldHint.propTypes = { children: PropTypes.node };

const DynamicForm = ({ formValues, handleChange }) => {
  const renderField = (field) => {
    const {
      type = "text",
      label,
      key,
      options = [],
      value,
      exportValues,
    } = field;

    const checkboxExportVal = field.exportValues?.[0];
    const normalizedValue = (Array.isArray(value) ? value : [value])
      .flat()
      .filter(Boolean);

    switch (type) {
      case "text":
        return (
          <StyledInput
            id={key}
            placeholder={`Enter ${label}`}
            value={value}
            onChange={(e) => handleChange(key, e.target.value)}
          />
        );

      case "dropdown":
        return (
          <StyledDropdown
            placeholder={`Select ${label}`}
            value={value || undefined}
            onChange={(val) => handleChange(key, val)}
            options={options.map((opt) => ({ label: opt, value: opt }))}
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
          />
        );

      case "radio":
        return (
          <StyledRadioGroup
            value={value}
            onChange={(e) => handleChange(key, e.target.value)}
          >
            {(options.length ? options : exportValues).map((opt) => {
              const label = typeof opt === "string" ? opt : opt.label;
              const value = typeof opt === "string" ? opt : opt.value;
              return (
                <Radio key={value} value={value}>
                  {label}
                </Radio>
              );
            })}
          </StyledRadioGroup>
        );

      case "checkbox":
        if (!checkboxExportVal) return null;

        return (
          <StyledCheckboxGroup
            options={[{ label: field.label, value: checkboxExportVal }]}
            value={normalizedValue}
            onChange={(selected) => {
              const newVal = selected.includes(checkboxExportVal)
                ? checkboxExportVal
                : "";
              console.log(
                "===>Checkbox changed:",
                field.name,
                "Selected:",
                selected,
                "Passing:",
                newVal
              );
              handleChange(field.key, newVal);
            }}
          />
        );

      default:
        return (
          <StyledUnsupportedField key={key}>
            ⚠️ Field type not available
          </StyledUnsupportedField>
        );
    }
  };

  return (
    <Row gutter={[24]}>
      {formValues.map((field) => {
        if (field.type === YES_NO_PAIR) {
          return (
            <Col key={field.key} span={24}>
              <FormGroup data-testid={`yes-no-${field.stem}`}>
                <Label>{field.label}</Label>
                <StyledRadioGroup
                  value={field.value || undefined}
                  onChange={(e) => {
                    handleChange(field.yesKey, e.target.value === "yes" ? "Yes" : "");
                    handleChange(field.noKey, e.target.value === "no" ? "Yes" : "");
                  }}
                >
                  <Radio value="yes">Yes</Radio>
                  <Radio value="no">No</Radio>
                </StyledRadioGroup>
                {field.hint && <FieldHint>{field.hint}</FieldHint>}
              </FormGroup>
            </Col>
          );
        }

        if (field.type === "checkboxGroup") {
          return (
            <Col key={`checkboxGroup-${field?.key}`} span={24}>
              <FormGroup>
                <Label>Check that apply</Label>
                <StyledCheckboxGroup
                  options={field.fields.map((f) => ({
                    label: f.label,
                    value: f.key,
                  }))}
                  value={field.fields
                    .filter((f) => f.value === f.exportValues?.[0])
                    .map((f) => f.key)}
                  onChange={(selectedKeys) => {
                    for (const f of field.fields) {
                      const isSelected = selectedKeys.includes(f.key);
                      const valueToSet = isSelected ? f.exportValues?.[0] : "";
                      handleChange(f.key, valueToSet);
                    }
                  }}
                />
              </FormGroup>
            </Col>
          );
        }

        return (
          <Col
            key={field.key}
            span={["text", "dropdown"].includes(field?.type) ? 12 : 24}
          >
            <FormGroup>
              <Label>{field.label}</Label>
              <FullWidth>{renderField(field)}</FullWidth>
            </FormGroup>
          </Col>
        );
      })}
    </Row>
  );
};

export default DynamicForm;

DynamicForm.propTypes = {
  formValues: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      type: PropTypes.string,
      label: PropTypes.string,
      key: PropTypes.string,
      value: PropTypes.any,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          value: PropTypes.string,
        })
      ),
    })
  ).isRequired,
  handleChange: PropTypes.func.isRequired,
};
